import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { getDatabase, ref as dbRef, onValue, remove, push } from "firebase/database";
import { getAuth } from "firebase/auth";
import FunctionsHeader from "../components/FunctionsHeader";
import Breadcrumb from "../components/Breadcrumb";
import PrintNutritionalLabelModal from "../components/PrintNutritionalLabelModal";

const HistoricoRoot = styled.div`
  width: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: var(--color-gray-200);
  font-family: var(--font-roboto);
`;

const MainContent = styled.div`
  flex: 1;
  padding: 15px 20px;
  display: flex;
  justify-content: center;
  
  @media (max-width: 768px) {
    padding: 10px 15px;
  }
`;

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 1400px;
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 25px;
  flex-wrap: wrap;
  gap: 15px;
  
  @media (max-width: 768px) {
    margin-bottom: 20px;
  }
`;

const Title = styled.h1`
  color: var(--color-white);
  font-size: 1.8rem;
  font-weight: 500;
  margin: 0;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const Subtitle = styled.p`
  color: var(--color-gray-300);
  font-size: 0.95rem;
  margin: 5px 0 0 0;
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const DevButton = styled.button`
  background-color: #28a745;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 5px;
  cursor: pointer;
  font-size: 0.85rem;
  transition: background-color 0.3s;

  &:hover {
    background-color: #218838;
  }
  
  @media (max-width: 768px) {
    padding: 6px 12px;
    font-size: 0.8rem;
  }
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 15px;
  margin-bottom: 25px;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 12px;
    margin-bottom: 20px;
  }
`;

const StatCard = styled.div`
  background-color: var(--color-white);
  border-radius: var(--br-5xs);
  padding: 18px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  text-align: center;
  
  @media (max-width: 768px) {
    padding: 15px;
  }
`;

const StatNumber = styled.div`
  font-size: 1.8rem;
  font-weight: bold;
  color: var(--color-darkorange);
  margin-bottom: 5px;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const StatLabel = styled.div`
  font-size: 0.85rem;
  color: var(--color-gray-600);
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;

const FiltersContainer = styled.div`
  background-color: var(--color-white);
  border-radius: var(--br-5xs);
  padding: 18px;
  margin-bottom: 18px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  
  @media (max-width: 768px) {
    padding: 15px;
    margin-bottom: 15px;
  }
`;

const FiltersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
  margin-bottom: 12px;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 10px;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const FilterLabel = styled.label`
  font-size: 0.85rem;
  color: var(--color-gray-600);
  margin-bottom: 4px;
  font-weight: 500;
`;

const FilterInput = styled.input`
  padding: 7px 10px;
  border: 1px solid var(--color-gray-300);
  border-radius: 4px;
  font-size: 0.85rem;

  &:focus {
    outline: none;
    border-color: var(--color-darkorange);
  }
`;

const FilterSelect = styled.select`
  padding: 7px 10px;
  border: 1px solid var(--color-gray-300);
  border-radius: 4px;
  font-size: 0.85rem;
  background-color: white;

  &:focus {
    outline: none;
    border-color: var(--color-darkorange);
  }
`;

const DateFiltersRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 12px;
  
  @media (max-width: 768px) {
    gap: 10px;
  }
`;

const CardsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 18px;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 15px;
  }
`;

const PrintCard = styled.div`
  background-color: var(--color-white);
  border-radius: var(--br-5xs);
  padding: 18px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s, box-shadow 0.2s;
  position: relative;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
  
  @media (max-width: 768px) {
    padding: 15px;
  }
`;

const PriorityBadge = styled.div`
  position: absolute;
  top: -8px;
  right: 12px;
  background-color: ${props => {
    switch (props.priority) {
      case 'recent': return '#28a745';
      case 'second': return '#17a2b8';
      case 'third': return '#ffc107';
      default: return 'transparent';
    }
  }};
  color: ${props => props.priority === 'third' ? '#333' : 'white'};
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.7rem;
  font-weight: bold;
  display: ${props => props.priority ? 'block' : 'none'};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
  margin-top: ${props => props.hasBadge ? '8px' : '0'};
`;

const CardTitle = styled.h3`
  margin: 0;
  color: var(--color-gray-800);
  font-size: 1.05rem;
  font-weight: 600;
  line-height: 1.3;
  flex: 1;
  margin-right: 10px;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const CardDate = styled.div`
  font-size: 0.75rem;
  color: var(--color-gray-500);
  text-align: right;
  white-space: nowrap;
`;

const CardInfo = styled.div`
  margin-bottom: 12px;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 6px;
  font-size: 0.85rem;
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;

const InfoLabel = styled.span`
  color: var(--color-gray-600);
  font-weight: 500;
`;

const InfoValue = styled.span`
  color: var(--color-gray-800);
  text-align: right;
`;

const CardActions = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  flex-wrap: wrap;
`;

const ActionButton = styled.button`
  padding: 7px 14px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 500;
  transition: all 0.3s;
  white-space: nowrap;

  ${props => props.primary ? `
    background-color: var(--color-darkorange);
    color: white;
    &:hover {
      background-color: #cc5500;
    }
  ` : `
    background-color: #dc3545;
    color: white;
    &:hover {
      background-color: #c82333;
    }
  `}
  
  @media (max-width: 768px) {
    padding: 6px 12px;
    font-size: 0.75rem;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 50px 20px;
  color: var(--color-gray-500);
  
  @media (max-width: 768px) {
    padding: 40px 15px;
  }
`;

const EmptyIcon = styled.div`
  font-size: 3.5rem;
  margin-bottom: 15px;
  
  @media (max-width: 768px) {
    font-size: 3rem;
  }
`;

const EmptyTitle = styled.h3`
  margin-bottom: 8px;
  color: var(--color-gray-600);
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const EmptyDescription = styled.p`
  font-size: 0.85rem;
  line-height: 1.4;
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 35px;
`;

const LoadingSpinner = styled.div`
  border: 4px solid #f3f3f3;
  border-top: 4px solid var(--color-darkorange);
  border-radius: 50%;
  width: 35px;
  height: 35px;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const HistoricoImpressao = () => {
  const navigate = useNavigate();
  const [printHistory, setPrintHistory] = useState([]);
  const [filteredHistory, setFilteredHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    clientName: "",
    recipeName: "",
    period: "",
    startDate: "",
    endDate: ""
  });
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [selectedPrintData, setSelectedPrintData] = useState(null);

  // Estatísticas calculadas
  const stats = {
    total: filteredHistory.length,
    thisMonth: filteredHistory.filter(item => {
      const itemDate = new Date(item.timestamp);
      const now = new Date();
      return itemDate.getMonth() === now.getMonth() && itemDate.getFullYear() === now.getFullYear();
    }).length,
    thisWeek: filteredHistory.filter(item => {
      const itemDate = new Date(item.timestamp);
      const now = new Date();
      const weekStart = new Date(now.setDate(now.getDate() - now.getDay()));
      return itemDate >= weekStart;
    }).length
  };

  useEffect(() => {
    loadPrintHistory();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [printHistory, filters]);

  const loadPrintHistory = () => {
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (!user) {
      setLoading(false);
      return;
    }

    const db = getDatabase();
    const historyRef = dbRef(db, `printHistory/${user.uid}`);
    
    onValue(historyRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        // Converter objeto em array e filtrar últimos 2 meses
        const twoMonthsAgo = new Date();
        twoMonthsAgo.setMonth(twoMonthsAgo.getMonth() - 2);
        
        const historyArray = Object.entries(data)
          .map(([id, item]) => ({ id, ...item }))
          .filter(item => new Date(item.timestamp) >= twoMonthsAgo)
          .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        
        setPrintHistory(historyArray);
      } else {
        setPrintHistory([]);
      }
      setLoading(false);
    });
  };

  const applyFilters = () => {
    let filtered = [...printHistory];

    // Filtro por nome do cliente
    if (filters.clientName) {
      filtered = filtered.filter(item => 
        item.clientName.toLowerCase().includes(filters.clientName.toLowerCase())
      );
    }

    // Filtro por nome da receita
    if (filters.recipeName) {
      filtered = filtered.filter(item => 
        item.recipeName.toLowerCase().includes(filters.recipeName.toLowerCase())
      );
    }

    // Filtro por período
    const now = new Date();
    if (filters.period) {
      switch (filters.period) {
        case 'today':
          filtered = filtered.filter(item => {
            const itemDate = new Date(item.timestamp);
            return itemDate.toDateString() === now.toDateString();
          });
          break;
        case 'week':
          const weekStart = new Date(now.setDate(now.getDate() - now.getDay()));
          filtered = filtered.filter(item => new Date(item.timestamp) >= weekStart);
          break;
        case 'month':
          filtered = filtered.filter(item => {
            const itemDate = new Date(item.timestamp);
            return itemDate.getMonth() === now.getMonth() && itemDate.getFullYear() === now.getFullYear();
          });
          break;
      }
    }

    // Filtro por datas específicas
    if (filters.startDate) {
      filtered = filtered.filter(item => new Date(item.timestamp) >= new Date(filters.startDate));
    }
    if (filters.endDate) {
      filtered = filtered.filter(item => new Date(item.timestamp) <= new Date(filters.endDate));
    }

    setFilteredHistory(filtered);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleReprint = (item) => {
    setSelectedPrintData({
      recipe: { id: item.recipeId, description: item.recipeName },
      nutritionalSummary: item.nutritionalSummary,
      initialData: {
        recipeName: item.recipeName,
        clientName: item.clientName,
        fabricationDate: item.fabricationDate,
        expirationDate: item.expirationDate,
        portionSize: item.portionSize,
        allergensText: item.allergensText,
        ingredients: item.ingredients
      }
    });
    setShowPrintModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Tem certeza que deseja excluir este registro do histórico?")) {
      const auth = getAuth();
      const user = auth.currentUser;
      
      if (user) {
        const db = getDatabase();
        const itemRef = dbRef(db, `printHistory/${user.uid}/${id}`);
        
        try {
          await remove(itemRef);
        } catch (error) {
          console.error("Erro ao excluir:", error);
          alert("Erro ao excluir o registro. Tente novamente.");
        }
      }
    }
  };

  const getPriorityBadge = (index) => {
    if (index === 0) return { priority: 'recent', text: '🔥 ÚLTIMA' };
    if (index === 1) return { priority: 'second', text: '🥈 PENÚLTIMA' };
    if (index === 2) return { priority: 'third', text: '🥉 TERCEIRA' };
    return { priority: null, text: '' };
  };

  // Função para adicionar dados de exemplo baseados no Firebase export
  const addSampleData = async () => {
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (!user) {
      alert("Usuário não autenticado");
      return;
    }

    const db = getDatabase();
    const historyRef = dbRef(db, `printHistory/${user.uid}`);

    // Dados de exemplo baseados no arquivo real do Firebase
    const sampleData = [
      {
        recipeId: "1246",
        recipeName: "Filé de frango com tomate cereja e manjericão, purê de inhame",
        clientName: "IGOR",
        fabricationDate: "2024-01-15",
        expirationDate: "2024-01-18",
        portionSize: 350,
        allergensText: "NÃO CONTÉM GLÚTEN/ CONTÉM LACTOSE.<br>ALÉRGICOS: PODE CONTER TRAÇOS DE GLÚTEN.",
        ingredients: "Filé de frango, tomate cereja, manjericão, inhame, azeite, sal",
        nutritionalSummary: {
          energy: 420,
          proteins: 35,
          carbohydrates: 25,
          dietaryFiber: 4,
          totalFats: 18,
          totalSugars: 8,
          addedSugars: 0,
          saturatedFats: 5,
          transFats: 0,
          sodium: 580
        },
        timestamp: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
        userId: user.uid
      },
      {
        recipeId: "1258",
        recipeName: "Filé de linguado com mix de sementes, arroz integral colorido",
        clientName: "CYNTIA",
        fabricationDate: "2024-01-20",
        expirationDate: "2024-01-23",
        portionSize: 320,
        allergensText: "CONTÉM GLÚTEN E SEMENTES.<br>ALÉRGICOS: PODE CONTER TRAÇOS DE LACTOSE.",
        ingredients: "Filé de linguado, arroz integral, sementes mistas, grão de bico, temperos",
        nutritionalSummary: {
          energy: 380,
          proteins: 28,
          carbohydrates: 32,
          dietaryFiber: 6,
          totalFats: 15,
          totalSugars: 5,
          addedSugars: 0,
          saturatedFats: 3,
          transFats: 0,
          sodium: 520
        },
        timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
        userId: user.uid
      },
      {
        recipeId: "1275",
        recipeName: "Tiras de filé de frango, cebola com mix de gergelim",
        clientName: "INGRID PERLINI", 
        fabricationDate: "2024-01-22",
        expirationDate: "2024-01-25",
        portionSize: 380,
        allergensText: "CONTÉM SEMENTES DE GERGELIM.<br>ALÉRGICOS: PODE CONTER TRAÇOS DE GLÚTEN E LACTOSE.",
        ingredients: "Filé de frango, cebola, gergelim preto e branco, arroz integral, purê de grão de bico",
        nutritionalSummary: {
          energy: 450,
          proteins: 38,
          carbohydrates: 28,
          dietaryFiber: 5,
          totalFats: 20,
          totalSugars: 6,
          addedSugars: 0,
          saturatedFats: 6,
          transFats: 0,
          sodium: 620
        },
        timestamp: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
        userId: user.uid
      }
    ];

    try {
      for (const item of sampleData) {
        await push(historyRef, item);
      }
      alert("Dados de exemplo adicionados com sucesso!");
    } catch (error) {
      console.error("Erro ao adicionar dados de exemplo:", error);
      alert("Erro ao adicionar dados de exemplo. Tente novamente.");
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("pt-BR");
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("pt-BR") + " às " + date.toLocaleTimeString("pt-BR", { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  if (loading) {
    return (
      <HistoricoRoot>
        <FunctionsHeader />
        <Breadcrumb path="Página inicial > Impressão > Histórico de Rótulos Nutricionais" />
        <LoadingContainer>
          <LoadingSpinner />
        </LoadingContainer>
      </HistoricoRoot>
    );
  }

  return (
    <HistoricoRoot>
      <FunctionsHeader />
      <Breadcrumb path="Página inicial > Impressão > Histórico de Rótulos Nutricionais" />
      
      <MainContent>
        <ContentWrapper>
          <Header>
            <div>
              <Title>📋 Histórico de Rótulos Nutricionais</Title>
              <Subtitle>Consulte e reimprima rótulos nutricionais dos últimos 2 meses</Subtitle>
            </div>
            {process.env.NODE_ENV === 'development' && (
              <DevButton onClick={addSampleData}>
                🧪 Adicionar Dados de Exemplo (Dev)
              </DevButton>
            )}
          </Header>

          <StatsContainer>
            <StatCard>
              <StatNumber>{stats.total}</StatNumber>
              <StatLabel>Total de Rótulos</StatLabel>
            </StatCard>
            <StatCard>
              <StatNumber>{stats.thisMonth}</StatNumber>
              <StatLabel>Este Mês</StatLabel>
            </StatCard>
            <StatCard>
              <StatNumber>{stats.thisWeek}</StatNumber>
              <StatLabel>Esta Semana</StatLabel>
            </StatCard>
          </StatsContainer>

          <FiltersContainer>
            <h3 style={{ margin: '0 0 12px 0', color: 'var(--color-gray-700)', fontSize: '1rem' }}>Filtros</h3>
            <FiltersGrid>
              <FilterGroup>
                <FilterLabel>Cliente</FilterLabel>
                <FilterInput
                  type="text"
                  placeholder="Nome do cliente"
                  value={filters.clientName}
                  onChange={(e) => handleFilterChange('clientName', e.target.value)}
                />
              </FilterGroup>
              <FilterGroup>
                <FilterLabel>Receita</FilterLabel>
                <FilterInput
                  type="text"
                  placeholder="Nome da receita"
                  value={filters.recipeName}
                  onChange={(e) => handleFilterChange('recipeName', e.target.value)}
                />
              </FilterGroup>
              <FilterGroup>
                <FilterLabel>Período</FilterLabel>
                <FilterSelect
                  value={filters.period}
                  onChange={(e) => handleFilterChange('period', e.target.value)}
                >
                  <option value="">Todos</option>
                  <option value="today">Hoje</option>
                  <option value="week">Esta semana</option>
                  <option value="month">Este mês</option>
                </FilterSelect>
              </FilterGroup>
            </FiltersGrid>
            <DateFiltersRow>
              <FilterGroup>
                <FilterLabel>Data início</FilterLabel>
                <FilterInput
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => handleFilterChange('startDate', e.target.value)}
                />
              </FilterGroup>
              <FilterGroup>
                <FilterLabel>Data fim</FilterLabel>
                <FilterInput
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => handleFilterChange('endDate', e.target.value)}
                />
              </FilterGroup>
            </DateFiltersRow>
          </FiltersContainer>

          {filteredHistory.length === 0 ? (
            <EmptyState>
              <EmptyIcon>📄</EmptyIcon>
              <EmptyTitle>Nenhum rótulo nutricional encontrado</EmptyTitle>
              <EmptyDescription>
                {printHistory.length === 0 
                  ? "Ainda não há registros de impressão de rótulos nutricionais. Comece imprimindo seu primeiro rótulo!"
                  : "Nenhum registro corresponde aos filtros aplicados. Tente ajustar os critérios de busca."
                }
              </EmptyDescription>
            </EmptyState>
          ) : (
            <CardsContainer>
              {filteredHistory.map((item, index) => {
                const badge = getPriorityBadge(index);
                return (
                  <PrintCard key={item.id}>
                    <PriorityBadge priority={badge.priority}>
                      {badge.text}
                    </PriorityBadge>
                    
                    <CardHeader hasBadge={badge.priority}>
                      <CardTitle>{item.recipeName}</CardTitle>
                      <CardDate>{formatDateTime(item.timestamp)}</CardDate>
                    </CardHeader>
                    
                    <CardInfo>
                      <InfoRow>
                        <InfoLabel>Impressão:</InfoLabel>
                        <InfoValue>{formatDateTime(item.timestamp)}</InfoValue>
                      </InfoRow>
                      <InfoRow>
                        <InfoLabel>Cliente:</InfoLabel>
                        <InfoValue>{item.clientName}</InfoValue>
                      </InfoRow>
                      <InfoRow>
                        <InfoLabel>Fabricação:</InfoLabel>
                        <InfoValue>{formatDate(item.fabricationDate)}</InfoValue>
                      </InfoRow>
                      <InfoRow>
                        <InfoLabel>Validade:</InfoLabel>
                        <InfoValue>{formatDate(item.expirationDate)}</InfoValue>
                      </InfoRow>
                      <InfoRow>
                        <InfoLabel>Porção:</InfoLabel>
                        <InfoValue>{item.portionSize}g</InfoValue>
                      </InfoRow>
                    </CardInfo>

                    <CardActions>
                      <ActionButton onClick={() => handleReprint(item)} primary>
                        🔄 Reimprimir
                      </ActionButton>
                      <ActionButton onClick={() => handleDelete(item.id)}>
                        🗑️ Excluir
                      </ActionButton>
                    </CardActions>
                  </PrintCard>
                );
              })}
            </CardsContainer>
          )}
        </ContentWrapper>
      </MainContent>

      {showPrintModal && selectedPrintData && (
        <PrintNutritionalLabelModal
          isOpen={showPrintModal}
          onClose={() => {
            setShowPrintModal(false);
            setSelectedPrintData(null);
          }}
          recipe={selectedPrintData.recipe}
          nutritionalSummary={selectedPrintData.nutritionalSummary}
          initialData={selectedPrintData.initialData}
          onPrintSuccess={() => {
            // Recarregar histórico após impressão bem-sucedida
            loadPrintHistory();
          }}
        />
      )}
    </HistoricoRoot>
  );
};

export default HistoricoImpressao;